import React, { useState } from "react";
import { formatArea } from "./locationArea";
import { ChevronDown, ChevronRight } from "lucide-react";
import PanelListHeader from "../components/PanelListHeader";

// Build a tree from flat list and render depth-first
function buildTree(rects) {
  const valid = rects.filter(r => r && r.id != null);
  const map = {};
  valid.forEach(r => { map[r.id] = { ...r, children: [] }; });
  const roots = [];
  valid.forEach(r => {
    if (r.parent_location_id != null && map[r.parent_location_id]) {
      map[r.parent_location_id].children.push(map[r.id]);
    } else {
      roots.push(map[r.id]);
    }
  });
  const sort = nodes => [...nodes].filter(Boolean).sort((a, b) => (a.id ?? 0) - (b.id ?? 0)).map(n => ({ ...n, children: sort(n.children) }));
  return sort(roots);
}

const FIELD_OPTIONS = [
  { key: "area_m2", label: "Area (m²)" },
  { key: "floorplan_id", label: "Floorplan ID" },
  { key: "name", label: "Name" },
  { key: "type", label: "Location type" },
  { key: "id", label: "Location ID" },
  { key: "pixels_per_meter", label: "Root px/m (stored)" },
  { key: "effective_pixels_per_meter", label: "Effective px/m (inherited)" },
  { key: "scale_source_location_id", label: "Scale source location ID" },
];

// A térkép láthatósági beállításai: loctagek, locationhatárok és a locationön kiírt mezők.
export const LocationVisibilityControls = ({
  locationHoverFields,
  setLocationHoverFields,
  editLoctagMode,
  setEditLoctagMode,
  setAddMode,
  loctagExtra = null,
  onNewLoctag,
}) => (
  <div className="space-y-3 px-3 py-3 text-xs text-slate-100">
    <div className="space-y-1 rounded-lg border border-slate-700 bg-slate-950/40 p-2">
      <PanelListHeader title="Loctags" onAdd={onNewLoctag} addLabel="New loctag" />
      <label className="flex items-center gap-2 text-[0.7rem] cursor-pointer px-1 py-1">
        <input
          type="checkbox"
          checked={!!editLoctagMode}
          onChange={(e) => {
            setEditLoctagMode && setEditLoctagMode(e.target.checked);
            if (!e.target.checked && setAddMode) setAddMode(false);
          }}
          className="w-3.5 h-3.5 accent-cyan-500"
        />
        <span>Show loctags</span>
      </label>
      {loctagExtra}
    </div>

    <div className="space-y-1 rounded-lg border border-slate-700 bg-slate-950/40 p-2">
      <PanelListHeader title="Show on canvas" />
      <label className="flex items-center gap-2 text-[0.7rem] cursor-pointer px-1 py-1">
        <input
          type="checkbox"
          checked={locationHoverFields.show_borders !== false}
          onChange={(e) => setLocationHoverFields(prev => ({ ...prev, show_borders: e.target.checked }))}
          className="w-3.5 h-3.5 accent-cyan-500"
        />
        <span>Location borders</span>
      </label>
      {FIELD_OPTIONS.map((field) => (
        <label key={field.key} className="flex items-center gap-2 text-[0.7rem] cursor-pointer px-1 py-1">
          <input
            type="checkbox"
            checked={!!locationHoverFields[field.key]}
            onChange={(e) => setLocationHoverFields(prev => ({ ...prev, [field.key]: e.target.checked }))}
            className="w-3.5 h-3.5 accent-cyan-500"
          />
          <span>{field.label}</span>
        </label>
      ))}
    </div>
  </div>
);

const IcleanViewLocationSettings = ({
  locationSettingsMode,
  setLocationSettingsMode,
  rects = [],
  selectedId = null,
  setSelectedId,
  setEditMode,
  drawMode,
  setDrawMode,
  locationTypes = [],
  onNameChange,
  onTypeChange,
  onFloorplanAction,
  onDelete,
  floorplanEditMode = false,
  floorplanEditLocation = null,
  onToggleCrop,
  onDeleteFloorplan,
  onSaveAndExit,
  technicalLocations = [],
  onTechnicalLocationHover,
  onSelectLocation,
  embedded = false,
  locationAreaById = new Map(),
  technicalAreaById = new Map(),
  onRootAreaChange,
}) => {
  const [collapsed, setCollapsed] = useState({});

  if (!locationSettingsMode && !embedded) return null;

  const selectedLocation = rects.find((r) => r.id === selectedId) || null;
  const tree = buildTree(rects);

  const toggleCollapse = (id, defaultCollapsed = false) => setCollapsed(prev => ({
    ...prev,
    [id]: !(prev[id] ?? defaultCollapsed),
  }));

  const renderNode = (node, depth = 0) => {
    if (!node || node.id == null) return null;
    const hasChildren = (node.children || []).length > 0;
    const isCollapsed = collapsed[node.id] ?? depth === 0;
    const isSelected = selectedId === node.id;
    return (
      <div key={node.id}>
        <div style={{ paddingLeft: depth * 12 }}>
          <button
            onClick={() => {
              setEditMode && setEditMode(true);
              setDrawMode && setDrawMode(false);
              onSelectLocation
                ? onSelectLocation(node.id)
                : setSelectedId && setSelectedId(node.id);
            }}
            className={`w-full text-left px-2 py-1 rounded border text-[0.65rem] flex items-center justify-between gap-1 ${
              isSelected
                ? "bg-red-700/30 border-red-400 text-red-100"
                : "bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700"
            }`}
          >
            <span className="truncate">{node.name || "New Location"} ({node.id})</span>
            {hasChildren && (
              <span
                className="flex-shrink-0 text-slate-400"
                onClick={e => { e.stopPropagation(); toggleCollapse(node.id, depth === 0); }}
              >
                {isCollapsed ? <ChevronRight size={10} /> : <ChevronDown size={10} />}
              </span>
            )}
          </button>
        </div>
        {hasChildren && !isCollapsed && (node.children || []).filter(Boolean).map(child => renderNode(child, depth + 1))}
      </div>
    );
  };

  return (
    <div className={`block w-full text-slate-100 text-xs ${embedded ? "px-3 pt-3" : "border-b border-slate-800 p-4"}`}>
      {!embedded && <div className="flex justify-between items-center mb-3">
        <div className="text-[0.7rem] text-slate-200 font-semibold uppercase tracking-wide">
          Location settings
        </div>
        <button
          onClick={() => setLocationSettingsMode(false)}
          className="w-5 h-5 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center text-slate-100 text-sm"
          title="Close"
        >
          x
        </button>
      </div>}

      <div className="space-y-2 mb-2">

        {/* Locations tree — always stays at the top of this section */}
        <div className="space-y-0.5 rounded-lg border border-slate-700 bg-slate-950/40 p-2">
          <PanelListHeader
            title="Locations"
            count={rects.length}
            addLabel="New location"
            onAdd={() => {
              setEditMode && setEditMode(true);
              setDrawMode && setDrawMode(true);
              setSelectedId && setSelectedId(null);
            }}
          />
          <div className="max-h-56 overflow-y-auto space-y-0.5 pr-1">
            {tree.map(node => renderNode(node, 0))}
          </div>
          {drawMode && (
            <div className="rounded border border-amber-400 bg-amber-300 px-2 py-1 text-[0.65rem] text-black">
              Drawing new location...
            </div>
          )}
        </div>

        {/* Selected location — directly below the stable tree */}
        {selectedLocation && (
          <div className="space-y-2 mb-2 p-2 border border-slate-700 rounded bg-slate-800/40">
          <div className="text-[0.6rem] text-slate-400">
            Selected location ({selectedLocation.id})
          </div>
          <input
            type="text"
            value={selectedLocation.name || ""}
            onChange={(e) => onNameChange && onNameChange(selectedLocation, e.target.value)}
            className="w-full text-[0.7rem] bg-slate-900 border border-slate-600 rounded px-2 py-1 text-slate-100"
            placeholder="Location name"
          />
          <select
            value={selectedLocation.type || ""}
            onChange={(e) => onTypeChange && onTypeChange(selectedLocation, e.target.value)}
            className="w-full text-[0.7rem] bg-slate-900 border border-slate-600 rounded px-2 py-1 text-slate-100"
          >
            <option value="">type</option>
            {(locationTypes.length
              ? locationTypes
              : ["building", "floor", "department", "room", "staircase", "elevator", "floorplan", "not defined"]
            ).filter((type) => type !== "technical").map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          {/* Floorplan: add if none, or toggle edit mode if exists */}
          {!selectedLocation.floorplan_id ? (
            <button
              onClick={() => onFloorplanAction && onFloorplanAction(selectedLocation)}
              className="w-full px-2 py-1 rounded border text-[0.7rem] bg-slate-800 text-slate-100 border-slate-600 hover:bg-slate-700"
            >
              + floorplan
            </button>
          ) : (
            <div className="space-y-1">
              <button
                onClick={() => onFloorplanAction && onFloorplanAction(selectedLocation)}
                className={`w-full px-2 py-1 rounded border text-[0.7rem] ${
                  floorplanEditMode && floorplanEditLocation?.id === selectedLocation.id
                    ? "bg-teal-700 text-white border-teal-500"
                    : "bg-slate-800 text-slate-100 border-slate-600 hover:bg-slate-700"
                }`}
              >
                floorplan edit
              </button>
              {floorplanEditMode && floorplanEditLocation?.id === selectedLocation.id && (
                <div className="flex gap-1">
                  <button
                    onClick={() => onToggleCrop && onToggleCrop()}
                    className="flex-1 px-2 py-1 rounded border text-[0.7rem] bg-slate-800 text-slate-100 border-slate-600 hover:bg-slate-700"
                  >
                    crop
                  </button>
                  <button
                    onClick={() => onDeleteFloorplan && onDeleteFloorplan()}
                    className="flex-1 px-2 py-1 rounded border text-[0.7rem] bg-red-900/40 text-red-300 border-red-700 hover:bg-red-800/50"
                  >
                    delete
                  </button>
                </div>
              )}
            </div>
          )}
          {!selectedLocation.parent_location_id && (
            <input
              type="text"
              value={selectedLocation.pixels_per_meter ?? "-"}
              readOnly
              className="w-full text-[0.7rem] bg-slate-900 border border-slate-600 rounded px-2 py-1 text-slate-100"
              placeholder="pixels_per_meter"
            />
          )}
          {!!selectedLocation.parent_location_id && (
            <input
              type="text"
              value={selectedLocation.effective_pixels_per_meter ?? "-"}
              readOnly
              className="w-full text-[0.7rem] bg-slate-900 border border-slate-600 rounded px-2 py-1 text-slate-100"
              placeholder="effective_pixels_per_meter"
            />
          )}
          {selectedLocation.is_root ? (
            <label className="flex items-center gap-2 text-[0.7rem] text-slate-300">
              <span className="whitespace-nowrap">Total area</span>
              <input
                key={`area-${selectedLocation.id}-${selectedLocation.area_m2 ?? ""}`}
                type="number"
                min="0"
                step="0.1"
                aria-label="Total area of the root location in square metres"
                defaultValue={selectedLocation.area_m2 ?? ""}
                placeholder="m²"
                onBlur={(e) => {
                  const next = e.target.value;
                  if (String(next) !== String(selectedLocation.area_m2 ?? "")) onRootAreaChange && onRootAreaChange(selectedLocation, next);
                }}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                className="w-full text-[0.7rem] bg-slate-900 border border-slate-600 rounded px-2 py-1 text-slate-100"
              />
              <span>m²</span>
            </label>
          ) : (
            <div className="flex justify-between px-1 text-[0.7rem] text-slate-300">
              <span>Area</span>
              <span className="tabular-nums">{formatArea(locationAreaById.get(String(selectedLocation.id))) || "set the root area"}</span>
            </div>
          )}
          <div className="space-y-1 border border-slate-700 rounded p-1.5 bg-slate-950/30">
            <div className="text-[0.7rem] font-semibold text-slate-300 px-1 pb-0.5">
              Technical locations
            </div>
            {technicalLocations.length > 0 ? technicalLocations.map((technicalLocation) => (
              <div
                key={technicalLocation.id}
                onMouseEnter={() => onTechnicalLocationHover && onTechnicalLocationHover(technicalLocation.id)}
                onMouseLeave={() => onTechnicalLocationHover && onTechnicalLocationHover(null)}
                className="rounded border border-slate-700 bg-slate-800 px-2 py-1 text-[0.65rem] text-slate-300 hover:border-red-400 hover:bg-red-900/20 hover:text-red-100"
              >
                <span className="flex justify-between gap-2">
                  <span className="truncate">{technicalLocation.name || `Technical location ${technicalLocation.id}`}</span>
                  {technicalAreaById.has(String(technicalLocation.id)) && (
                    <span className="shrink-0 tabular-nums text-slate-400">{formatArea(technicalAreaById.get(String(technicalLocation.id)))}</span>
                  )}
                </span>
              </div>
            )) : (
              <div className="px-1 py-1 text-[0.65rem] text-slate-500">
                Nincs technikai location.
              </div>
            )}
          </div>
          <button
            onClick={() => onDelete && onDelete(selectedLocation)}
            className="w-full px-2 py-1 rounded border border-red-500 bg-red-600/20 text-red-300 text-[0.7rem] hover:bg-red-600/30"
          >
            Delete selected location
          </button>
        </div>
        )}

      </div>
    </div>
  );
};

export default IcleanViewLocationSettings;
